import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Box, Grid } from "@mui/system";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import OverviewConversions, {
  OverviewConversionsRef,
} from "./Conversions/OverviewConversions";
import OverviewAccountSummary, {
  OverviewAccountSummaryRef,
} from "./AccountSummary/OverviewAccountSummary";
import {
  selectBizOverviewAccountSummaryLoading,
  selectBizOverviewConversionsLoading,
} from "../../../../../features/bizDevelopmentDashboardSlice";

const OVERVIEW_TAB_KEYS = ["account_summary", "conversions"];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const OverviewTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const overviewTabFromUrl = searchParams.get("overviewTab");
  const initialTab = overviewTabFromUrl
    ? Math.max(OVERVIEW_TAB_KEYS.indexOf(overviewTabFromUrl), 0)
    : 0;
  const [value, setValue] = React.useState(initialTab);
  const accountSummaryRef = React.useRef<OverviewAccountSummaryRef>(null);
  const conversionsRef = React.useRef<OverviewConversionsRef>(null);
  const isAccountSummaryLoading = useSelector(
    selectBizOverviewAccountSummaryLoading,
  );
  const isConversionsLoading = useSelector(selectBizOverviewConversionsLoading);
  const isRefreshDisabled =
    value === 0 ? isAccountSummaryLoading : isConversionsLoading;

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearchParams((prev) => {
      prev.set("overviewTab", OVERVIEW_TAB_KEYS[newValue]);
      return prev;
    });
  };

  const handleRefresh = () => {
    if (value === 0) {
      accountSummaryRef.current?.refresh();
      return;
    }
    conversionsRef.current?.refresh();
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" px={2} py={1.5}>
          <Grid size={6}>
            <Box display={"flex"} alignItems={"center"}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                sx={{
                  background: "#F5F5F5",
                  borderRadius: "8px",
                  padding: "4px",
                  minHeight: "30px",
                  "& .MuiTab-root": {
                    color: "#00000061",
                    padding: "5px 16px",
                    minHeight: "30px",
                    "&.Mui-selected": {
                      color: "#000000DE",
                      background: "#ffffff",
                      borderRadius: "4px",
                    },
                  },

                  /* Active line color */
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#9C27B0",
                    height: "0px", // optional
                    borderRadius: "10px", // optional
                  },
                }}
              >
                <Tab
                  data-tracker="833"
                  iconPosition="start"
                  label="ACCOUNT SUMMARY"
                  {...a11yProps(0)}
                />
                <Tab
                  data-tracker="834"
                  iconPosition="start"
                  label="CONVERSIONS"
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box display={"flex"} justifyContent={"flex-end"}>
              <IconButton
                aria-label="refresh"
                color="secondary"
                disabled={isRefreshDisabled}
                onClick={handleRefresh}
                data-tracker="837"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <CustomTabPanel value={value} index={0}>
          <OverviewAccountSummary ref={accountSummaryRef} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <OverviewConversions ref={conversionsRef} />
        </CustomTabPanel>
      </Box>
    </>
  );
};

export default OverviewTabs;
